import React, { useState, useEffect } from "react"
import { Button, Modal } from "react-bootstrap"
import { defineMessages, FormattedMessage, injectIntl } from "react-intl"
import Icon from "../../common/icon/icon.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import { getPriceListName } from "../../name-helpers/priceListNameHelper"
import CustomerSelect from "../../customer/selection.component.jsx"
import { Intl } from "../../state/helpers/IntlProps"
import QuoteStatusSelect from "../../quote/statusesselection.component.jsx"
import { generateOptions } from "../../quote/utils/QuoteStatusOrderForm"
import { PROPOSAL_TYPE } from "../../state/proposal/ProposalType"

const messages = defineMessages({
    modal_title: {
        id: "price_list.modal.creation_title",
        defaultMessage: "Create a {priceList}",
    },
    title_label: {
        id: "price_list.modal.title_label",
        defaultMessage: "Title",
    },
    object_type_label: {
        id: "price_list.modal.object_type_label",
        defaultMessage: "Will create",
    },
    title_placeholder: {
        id: "price_list.modal.creation.title_placeholder",
        defaultMessage: "i.e: Weekly special offer",
    },
    "object_type_invoice_placeholder": {
        id: "price_list.modal.creation.object_type_invoice",
        defaultMessage: "Invoice",
    },
    "object_type_quote_placeholder": {
        id: "price_list.modal.creation.object_type_quote",
        defaultMessage: "Quote",
    },
    "object_type_credit-memo_placeholder": {
        id: "price_list.modal.creation.object_type_credit_memo",
        defaultMessage: "Credit memo (deposit)",
    },
    credit_memo_receiver: {
        id: "price_list.modal.credit_memo_receiver_label",
        defaultMessage: "Credit memo receiver",
    },
    buyer_account: {
        id: "price_list.modal.buyer_account_label",
        defaultMessage: "Buyer's account",
    },
    status: {
        id: "price_list.modal.status",
        defaultMessage: "Status",
    }

})


type Props = {
    intl: Intl
    title: string
    objectType: string
    hideSelect: boolean
    isLoading: boolean
    onHide: () => boolean
    onCreate: (title: string, objectType: string, creditMemoContactReceiverId: string, docGeneratedStatus: string) => void
}

const objectTypes = ["invoice", "quote", "credit-memo"]

const NewPriceListModal = (props: Props) => {
 
    const [title, setTitle] = useState(props.title)
    const [objectType, setObjectType] = useState(props.objectType)
    const [creditMemoContactReceiverId, setCreditMemoContactReceiverId] = useState<string>("")
    const [docGeneratedStatus, setDocGeneratedStatus] = useState<string>("new")

    const options = generateOptions(props.intl)
    useEffect(() => {
        if (objectType !== "credit-memo") {
            setCreditMemoContactReceiverId("")
        }
    }, [objectType])

    return (
        <Modal {...props}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FormattedMessage{...messages.modal_title}
                        values={{
                            priceList: getPriceListName(props.intl)
                        }} />
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="container-fluid">
                    <div className="form-group row mt-3">
                        <label className="col-lg-3 col-12 col-form-label">
                            <FormattedMessage{...messages.title_label} />
                        </label>

                        <div className="col-lg-9 col-12">
                            <input
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                name={"title"}
                                type="text"
                                className="form-control"
                                placeholder={props.intl.formatMessage(messages["title_placeholder"])} />
                        </div>
                    </div>

                    {!props.hideSelect &&
                        <div className="form-group row mt-3">
                            <label className="col-lg-3 col-12 col-form-label">
                                <FormattedMessage{...messages.object_type_label} />
                            </label>

                            <div className="col-lg-9 col-12">
                                <select
                                    value={objectType}
                                    onChange={e => setObjectType(e.target.value)}
                                    name={"objectType"}
                                    className="form-control">
                                    {objectTypes.map((type, i) => {
                                        return (
                                            <option key={i} value={type}>
                                                {props.intl.formatMessage(messages[`object_type_${type}_placeholder`])}
                                            </option>
                                        )
                                    }
                                    )}
                                </select>
                            </div>
                        </div>}

                    {objectType === PROPOSAL_TYPE.CREDIT_MEMO_RECEIPT &&
                        <div className="form-group row mt-3">
                            <label className="col-lg-3 col-12 col-form-label">
                                <FormattedMessage{...messages.credit_memo_receiver} />
                            </label>
                            <div className="col-lg-9 col-12">
                                <CustomerSelect
                                    value={creditMemoContactReceiverId}
                                    placeholder={messages.buyer_account}
                                    onChange={(contactId) => {
                                        setCreditMemoContactReceiverId(contactId)
                                    }}
                                />
                                <small className="text-secondary">
                                    <em>
                                        <FormattedMessage
                                            id="price_list.modal.buyer_account.description"
                                            defaultMessage="Leave empty to create credit memos on the buyer's account"
                                        />
                                    </em>
                                </small>
                            </div>
                        </div>}
                    {objectType === PROPOSAL_TYPE.QUOTE &&
                        <div className="form-group row mt-3">
                            <label className="col-lg-3 col-12 col-form-label">
                                <FormattedMessage{...messages.status} />
                            </label>
                            <div className="col-lg-9 col-12">
                                <QuoteStatusSelect
                                    multi={false}
                                    value={docGeneratedStatus}
                                    onChange={(docGeneratedStatus) => {
                                        setDocGeneratedStatus(docGeneratedStatus)
                                    }}
                                    options={options}
                                />
                            </div>
                        </div>}

                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    onClick={props.onHide}
                    variant="secondary">
                    Close
                </Button>

                <Button
                    onClick={() => props.onCreate(title, objectType, creditMemoContactReceiverId, docGeneratedStatus)}
                    variant="primary"
                    disabled={props.isLoading || !title}>
                    {props.isLoading ?
                        <LoadingSpinner
                            height={15}
                            width={3}
                            className="px-5" /> :
                        <>
                            <Icon value={"shopping-cart"} className={"mr-1"} />
                            <FormattedMessage {...messages.modal_title}
                                values={{
                                    priceList: getPriceListName(props.intl)
                                }} />
                        </>}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

NewPriceListModal.defaultProps = {
    objectType: "invoice",
    hideSelect: false,
    isLoading: false,
    title: "",
}

export default injectIntl(NewPriceListModal)





// WEBPACK FOOTER //
// ./src/price-list/components/NewPriceListModal.tsx